// Alcance por dimensión de una política: arma las columnas del mock con las piezas compartidas de
// `shared/patterns/ScopeChips`. "Todos" = lista vacía en el modelo y AUSENTE en el wire; el chip
// "any" se enciende cuando no hay selección.
import { useState } from 'react';
import {
    ScopeChip,
    ScopeChipGrid,
    ScopeChipGroup,
    ScopeChipSearch,
} from '@/shared/patterns/ScopeChips';
import type { ScopeOption } from '../../promotions/api/useScopeCatalog';
import { planLevelTone, segmentSlotTone } from '../../scopeTones';
import { SegmentCatalogNotice } from '../../segments/components/SegmentCatalogNotice';
import { segmentCountText } from '../../segments/logic';
import type { SegmentCatalogState } from '../../segments/types';
import {
    ALL_SEGMENTS_LABEL,
    DEFAULT_SEGMENT_SCOPE_DETAIL,
    POLICY_LEVEL_LABEL,
    type PolicyLevel,
    type Segment,
} from '../types';

/** Toggle multi-select con "any": vacío = todos; quitar el último vuelve a "todos". */
function toggleIn<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * Los niveles que se dejan elegir. `ALL` NO está: el motor compara `targetLevels.includes(nivel)`,
 * así que una política guardada con `['ALL']` no alcanza a ninguna venta. «Todos» es la lista vacía.
 */
const SELECTABLE_LEVELS: readonly PolicyLevel[] = ['BRONZE', 'SILVER', 'GOLD'];

/**
 * Los segmentos ELEGIBLES, en orden alfabético. El por omisión queda fuera: el resolve nunca lo
 * asigna a un cliente, así que acotar una política a él la deja sin alcanzar a nadie.
 */
function selectableByName(segments: readonly Segment[]): Segment[] {
    return [...segments]
        .filter((segment) => !segment.isDefault)
        .sort((left, right) => left.name.localeCompare(right.name, 'es-MX'));
}

/** Filtra por el `haystack` del catálogo, que ya trae nombre, código e id en minúsculas. */
function search(options: readonly ScopeOption[], term: string): ScopeOption[] {
    const needle = term.toLowerCase().trim();
    if (needle === '') return [...options];
    return options.filter((option) => option.haystack.includes(needle));
}

export function ScopeChips({
    segments,
    stores,
    products,
    segmentCatalogState,
    storesLoading,
    productsLoading,
    targetLevels,
    storeIds,
    productSkus,
    segmentIds,
    disabled,
    onChange,
}: {
    segments: readonly Segment[];
    stores: readonly ScopeOption[];
    products: readonly ScopeOption[];
    /** Cómo llegó el catálogo prestado de segmentos. Solo `ready` autoriza a elegir. */
    segmentCatalogState: SegmentCatalogState;
    storesLoading: boolean;
    productsLoading: boolean;
    targetLevels: PolicyLevel[];
    storeIds: string[];
    productSkus: string[];
    segmentIds: string[];
    disabled: boolean;
    onChange: (patch: {
        targetLevels?: PolicyLevel[];
        storeIds?: string[];
        productSkus?: string[];
        segmentIds?: string[];
    }) => void;
}) {
    const [storeSearch, setStoreSearch] = useState('');
    const [productSearch, setProductSearch] = useState('');

    const visibleStores = search(stores, storeSearch);
    const visibleProducts = search(products, productSearch);

    // Un id guardado que ya no está en el catálogo se pinta igual: esconderlo haría creer que el
    // alcance es más ancho de lo que es, y guardar lo borraría sin que nadie lo pidiera.
    const missingStores = storeIds.filter((id) => !stores.some((option) => option.value === id));
    const missingProducts = productSkus.filter(
        (sku) => !products.some((option) => option.value === sku),
    );
    const selectableSegments = selectableByName(segments);
    const defaultSegmentId = segments.find((segment) => segment.isDefault)?.id ?? null;
    // El por omisión guardado entra acá igual que un id borrado: se pinta para que el operador lo
    // vea y lo corrija, con el detalle que declara que ese alcance quedó vacío.
    const missingSegments = segmentIds.filter(
        (id) => !selectableSegments.some((segment) => segment.id === id),
    );

    // Sin catálogo leído el selector no elige: un vacío editable haría pasar por decisión del
    // operador un «todos los segmentos» que en realidad es un hueco.
    const segmentsDisabled = disabled || segmentCatalogState !== 'ready';

    return (
        <ScopeChipGrid>
            <ScopeChipGroup title="Segmento">
                <SegmentCatalogNotice state={segmentCatalogState} place="picker" />
                <ScopeChip
                    dim="segment"
                    label={ALL_SEGMENTS_LABEL}
                    selected={segmentIds.length === 0}
                    disabled={segmentsDisabled}
                    onToggle={() => {
                        onChange({ segmentIds: [] });
                    }}
                />
                {missingSegments.map((id) => (
                    <ScopeChip
                        key={id}
                        dim="segment"
                        label={id}
                        detail={
                            id === defaultSegmentId
                                ? DEFAULT_SEGMENT_SCOPE_DETAIL
                                : 'Fuera del catálogo de segmentos'
                        }
                        selected
                        disabled={segmentsDisabled}
                        onToggle={() => {
                            onChange({ segmentIds: toggleIn(segmentIds, id) });
                        }}
                    />
                ))}
                {selectableSegments.map((segment) => (
                    <ScopeChip
                        key={segment.id}
                        dim="segment"
                        label={segment.name}
                        detail={segmentCountText(segment)}
                        tone={segmentSlotTone(segment.colorSlot)}
                        selected={segmentIds.includes(segment.id)}
                        disabled={segmentsDisabled}
                        onToggle={() => {
                            onChange({ segmentIds: toggleIn(segmentIds, segment.id) });
                        }}
                    />
                ))}
            </ScopeChipGroup>

            <ScopeChipGroup title="Nivel de riesgo">
                <ScopeChip
                    dim="tier"
                    label="Todos los niveles"
                    selected={targetLevels.length === 0}
                    disabled={disabled}
                    onToggle={() => {
                        onChange({ targetLevels: [] });
                    }}
                />
                {SELECTABLE_LEVELS.map((level) => (
                    <ScopeChip
                        key={level}
                        dim="tier"
                        label={POLICY_LEVEL_LABEL[level]}
                        tone={planLevelTone(level)}
                        selected={targetLevels.includes(level)}
                        disabled={disabled}
                        onToggle={() => {
                            onChange({ targetLevels: toggleIn(targetLevels, level) });
                        }}
                    />
                ))}
            </ScopeChipGroup>

            <ScopeChipGroup
                title="Tienda / canal"
                search={
                    <ScopeChipSearch
                        value={storeSearch}
                        placeholder="Buscar tienda…"
                        onChange={setStoreSearch}
                    />
                }
            >
                <ScopeChip
                    dim="store"
                    label="Todas las tiendas"
                    selected={storeIds.length === 0}
                    disabled={disabled}
                    onToggle={() => {
                        onChange({ storeIds: [] });
                    }}
                />
                {storesLoading ? (
                    <span className="text-[10.5px] text-muted-foreground">Cargando tiendas…</span>
                ) : null}
                {missingStores.map((id) => (
                    <ScopeChip
                        key={id}
                        dim="store"
                        label={id}
                        detail="Fuera del padrón de tiendas"
                        selected
                        disabled={disabled}
                        onToggle={() => {
                            onChange({ storeIds: toggleIn(storeIds, id) });
                        }}
                    />
                ))}
                {visibleStores.map((option) => (
                    <ScopeChip
                        key={option.value}
                        dim="store"
                        label={option.label}
                        detail={option.detail}
                        selected={storeIds.includes(option.value)}
                        disabled={disabled}
                        onToggle={() => {
                            onChange({ storeIds: toggleIn(storeIds, option.value) });
                        }}
                    />
                ))}
            </ScopeChipGroup>

            <ScopeChipGroup
                title="Producto"
                search={
                    <ScopeChipSearch
                        value={productSearch}
                        placeholder="Buscar SKU…"
                        onChange={setProductSearch}
                    />
                }
            >
                <ScopeChip
                    dim="product"
                    label="Todos los productos"
                    selected={productSkus.length === 0}
                    disabled={disabled}
                    onToggle={() => {
                        onChange({ productSkus: [] });
                    }}
                />
                {productsLoading ? (
                    <span className="text-[10.5px] text-muted-foreground">Cargando productos…</span>
                ) : null}
                {missingProducts.map((sku) => (
                    <ScopeChip
                        key={sku}
                        dim="product"
                        label={sku}
                        detail="Fuera del catálogo de productos"
                        selected
                        disabled={disabled}
                        onToggle={() => {
                            onChange({ productSkus: toggleIn(productSkus, sku) });
                        }}
                    />
                ))}
                {visibleProducts.map((option) => (
                    <ScopeChip
                        key={option.value}
                        dim="product"
                        label={option.label}
                        detail={option.detail}
                        selected={productSkus.includes(option.value)}
                        disabled={disabled}
                        onToggle={() => {
                            onChange({ productSkus: toggleIn(productSkus, option.value) });
                        }}
                    />
                ))}
            </ScopeChipGroup>
        </ScopeChipGrid>
    );
}
